import { Injectable, inject } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { ServiceBase } from '@core/services/service-base';
import { Observable, map } from 'rxjs';
import { BackendTemplate, LibraryPage, ReportTemplate, TemplateImage } from '../interfaces';
import { String } from 'typescript-string-operations';
import { IPagedResult } from '@shared/interfaces/paged-result.interface';
import { withSpinner } from '@shared/operators/with-spinner.operator';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import { AppConstants } from '@core/constants';

@Injectable({
  providedIn: 'root',
})
export class ReportingApiService extends ServiceBase {
  private _apiBaseService = inject(ApiBaseService);
  private readonly customRendererService = inject(CustomRendererService);

  listPages(): Observable<LibraryPage[]> {
    return this._apiBaseService
      .getJson<IPagedResult<LibraryPage>>(this._appConstants.API.REPORTING.PAGES.LIST)
      .pipe(map((pagedResult) => pagedResult.results));
  }

  listTemplates(): Observable<BackendTemplate[]> {
    return this._apiBaseService
      .getJson<IPagedResult<BackendTemplate>>(this._appConstants.API.REPORTING.TEMPLATES.LIST)
      .pipe(map((pagedResult) => pagedResult.results));
  }

  getTemplate(templateId: number, expandPages = true): Observable<BackendTemplate> {
    let apiUrl: string = String.Format(this._appConstants.API.REPORTING.TEMPLATES.GET, templateId);
    if (expandPages) {
      const params: URLSearchParams = new URLSearchParams({ expand: 'pages' });
      apiUrl += `?${params.toString()}`;
    }
    return this._apiBaseService.getJson<BackendTemplate>(apiUrl);
  }

  createTemplate(template: Partial<BackendTemplate>): Observable<BackendTemplate> {
    return this._apiBaseService
      .postJson<BackendTemplate>(this._appConstants.API.REPORTING.TEMPLATES.CREATE, template)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  updateTemplate(templateId: number, template: Partial<BackendTemplate>): Observable<BackendTemplate> {
    const apiUrl: string = String.Format(this._appConstants.API.REPORTING.TEMPLATES.UPDATE, templateId);
    return this._apiBaseService
      .putJson<BackendTemplate>(apiUrl, template)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  deleteTemplate(templateId: number): Observable<void> {
    const apiUrl: string = String.Format(this._appConstants.API.REPORTING.TEMPLATES.DELETE, templateId);
    return this._apiBaseService
      .deleteJson<void>(apiUrl)
      .pipe(withSpinner(this.customRendererService, AppConstants.MAT_SPINNER_CLASS));
  }

  uploadTemplateImage(templateId: number, file: File): Observable<TemplateImage> {
    const apiUrl: string = String.Format(this._appConstants.API.REPORTING.TEMPLATES.UPLOAD_IMAGE, templateId);
    const formData = new FormData();
    formData.append('image', file);

    return this._apiBaseService.postMultipart<TemplateImage>(apiUrl, formData);
  }

  deleteTemplateImage(templateId: number, imageId: number): Observable<{ id: number }> {
    const apiUrl: string = String.Format(this._appConstants.API.REPORTING.TEMPLATES.DELETE_IMAGE, templateId, imageId);
    return this._apiBaseService.deleteJson<{ id: number }>(apiUrl);
  }
}
